import type { CompletionClarity, CompletionConsideration } from '@year13/kui/composables/useCompletionSteps';
import type { Sentiment } from '@year13/kui/types/feedback';
import type { Ref } from 'vue';

import { CombinedError } from '@urql/core';
import { useQuery } from '@urql/vue';

import type { AnalyticsOrigin } from '~/constants/analyticsOrigin';
import type { LearningAssetViewModel } from '~/types/unified';

import { useAnalyticsService } from '~/composables/core';
import { useUserProgress } from '~/composables/Learn/Progress/useUserProgress';
import { useActivityOrchestrator } from '~/composables/unified/useActivityOrchestrator';
import { useConsiderationAreas } from '~/composables/unified/useConsiderationAreas';
import { useUnifiedErrorHandler } from '~/composables/unified/useUnifiedErrorHandler';
import { useUserAvatar } from '~/composables/unified/useUserAvatar';
import { EVENTS } from '~/constants/events';
import { earnedAwardFragment } from '~/graphql/documents/awardWallet';
import { coinValueFragment } from '~/graphql/documents/content';
import { publicFileFragment } from '~/graphql/documents/core';
import { feedbackSurveyDefinitionFragment } from '~/graphql/documents/feedbackSurvey';
import { userProgressFragment } from '~/graphql/documents/learn';
import { fetchLearningAsset } from '~/graphql/documents/learn/fetchLearningAssets';
import { seoMetaFragment } from '~/graphql/documents/learningContent';
import { useFragment } from '~/graphql/generated';
import { CoachActivityInfoType, CompletableType } from '~/graphql/generated/graphql';
import { learningAssetTypeTransformer } from '~/utils/transformers/learningAssetTransformers';
import { transformLearningContent } from '~/utils/transformers/learningContentTransformers';
import { ulid } from '~/utils/ulid';
import { isSkillAward } from '~/utils/unified/awardHelpers';
import { computeDurationSeconds } from '~/utils/unified/computeDurationSeconds';
import { buildCompletionConsiderations } from '~/utils/unified/considerationProgress';
import { mapSpotlight } from '~/utils/unified/mapSpotlight';
import { mapSkillsToViewModel, parseLearningOutcomes } from '~/utils/unified/overviewMappers';
import { trackCoinsEarned } from '~/utils/unified/trackCoinsEarned';

export type LearningAssetScreen = 'overview' | 'content' | 'sentiment' | 'completion' | 'survey';

export interface LearningAssetFlowOptions {
  skipOverview?: boolean;
  /**
   * Asked once the asset has loaded, when `skipOverview` is set: an asset this says yes to opens on
   * its overview after all, and the student starts it from there.
   */
  keepOverview?: (asset: LearningAssetViewModel) => boolean;
  bySlug?: boolean;
  origin?: AnalyticsOrigin;
  /**
   * The screen this activity opens on. `survey` is the coach's survey card arriving: the student
   * finished this activity already, so the questionnaire is all there is to show.
   */
  initialScreen?: 'survey';
}

// Re-exported so the public learn-asset page can run the same query during SSR (priming
// the URQL graphcache + ssrExchange) without duplicating the document reference.
export const assetQueryDocument = fetchLearningAsset;

// Shared by useLearningAssetFlow's reactive query and the page-level SSR prefetch, so the
// two never drift on filter shape/variables.
export const buildAssetQueryVariables = (id: string, opts?: { bySlug?: boolean }) =>
  opts?.bySlug ? { filters: { slugs: [id] } } : { filters: { ids: [id] } };

/** What finishing an activity returned: what it paid, and what it moved on the student's profile. */
export interface LearningAssetCompletionResult {
  awardedCoins: number | null;
  totalCoins: number | null;
  earnedSkills: Array<{ id: string; name: string; iconUrl: string | null }>;
  considerations: CompletionConsideration[];
  clarity: CompletionClarity | null;
  wasReCompletion: boolean;
}

/** The asset identity every asset event carries. */
export interface LearningAssetEventProps {
  assetId: string | null;
  assetSlug: string | null;
  assetName: string | null;
  assetType: string | null;
  assetTemplate: string | null;
}

export const useLearningAssetFlow = (
  learningAssetId: Ref<string>,
  options?: LearningAssetFlowOptions,
) => {
  const orchestrator = useActivityOrchestrator()!;
  const previewMode = orchestrator.previewMode ?? false;
  // One sitting with one asset. Collections nest: advancing A→B unmounts A's flow and mounts
  // B's, so B gets a fresh id rather than inheriting A's.
  const learningSessionId = ulid();
  const initialScreen: LearningAssetScreen = options?.initialScreen ?? (options?.skipOverview ? 'content' : 'overview');
  const screen = ref<LearningAssetScreen>(initialScreen);
  const hasStarted = ref(!options?.initialScreen && !!options?.skipOverview);
  const showCloseConfirmation = ref(false);
  const progressId = ref<string>();
  const completionResult = ref<LearningAssetCompletionResult | null>(null);

  // `stale` is the second half of `fetching` under `cache-and-network`: the cache answers first and
  // urql calls that answer delivered — `fetching` goes false — while the network request behind it is
  // still out. A read that has produced nothing is only a miss once BOTH are false.
  const { data, fetching, stale, error } = useQuery({
    query: fetchLearningAsset,
    variables: computed(() => buildAssetQueryVariables(learningAssetId.value, { bySlug: options?.bySlug })),
    requestPolicy: 'cache-and-network',
  });

  const rawAsset = computed(() => data.value?.learningAssets?.data?.[0] ?? null);
  const resolvedId = computed(() => rawAsset.value?.id ?? learningAssetId.value);

  const asset = computed<LearningAssetViewModel | null>(() => {
    if (!rawAsset.value) {
      return null;
    }

    const coinValue = rawAsset.value.coinValue
      ? useFragment(coinValueFragment, rawAsset.value.coinValue)?.coins ?? null
      : null;

    const coverImage = rawAsset.value.coverImage
      ? useFragment(publicFileFragment, rawAsset.value.coverImage)?.publicUrl ?? null
      : null;

    const meta = rawAsset.value.meta
      ? useFragment(seoMetaFragment, rawAsset.value.meta)
      : null;

    return {
      id: resolvedId.value,
      name: rawAsset.value.name ?? '',
      slug: rawAsset.value.slug ?? '',
      type: learningAssetTypeTransformer(rawAsset.value.type),
      template: rawAsset.value.templateName ?? null,
      duration: rawAsset.value.duration ?? null,
      coins: coinValue,
      coverImageUrl: coverImage,
      publishedContent: transformLearningContent(rawAsset.value.publishedContent),
      skills: mapSkillsToViewModel(rawAsset.value.publishedSkills),
      learningOutcomes: rawAsset.value.structuredLearningOutcome
        ? [{
            title: rawAsset.value.structuredLearningOutcome.title,
            description: rawAsset.value.structuredLearningOutcome.description ?? null,
            assetId: null,
            iconUrl: null,
          }]
        : parseLearningOutcomes(rawAsset.value.learningOutcomes),
      structuredLearningOutcome: rawAsset.value.structuredLearningOutcome
        ? { title: rawAsset.value.structuredLearningOutcome.title, description: rawAsset.value.structuredLearningOutcome.description ?? null }
        : null,
      description: meta?.description ?? null,
      partnerId: rawAsset.value.partnerId ?? null,
      partnerName: rawAsset.value.partner?.name ?? null,
      spotlight: mapSpotlight(rawAsset.value.spotlight),
    };
  });

  /** The questionnaire standing on this activity, asked after the slider or opened by the coach's survey card. */
  const assetSurvey = computed(() =>
    useFragment(feedbackSurveyDefinitionFragment, rawAsset.value?.feedbackSurvey) ?? null);

  /** What this viewer's externally-answered survey on this activity paid, once redeemed. */
  const assetSurveyCoinsAwarded = computed(() => rawAsset.value?.externalFeedbackSurveyCoinsAwarded ?? null);

  const previouslyCompleted = computed(() => {
    const progress = rawAsset.value?.currentUserProgress
      ? useFragment(userProgressFragment, rawAsset.value.currentUserProgress)
      : null;
    return !!progress?.completedAt;
  });

  const baseAssetEventProps = (): LearningAssetEventProps => ({
    assetId: resolvedId.value ?? null,
    assetSlug: asset.value?.slug ?? null,
    assetName: asset.value?.name ?? null,
    assetType: asset.value?.type ?? null,
    assetTemplate: asset.value?.template ?? null,
  });

  const { startProgress, completeProgress } = useUserProgress();
  const { handleError } = useUnifiedErrorHandler();
  const analytics = useAnalyticsService();
  const { accountStudentRole } = useAuth();
  const { avatarIllustration } = useUserAvatar();
  // `allConsiderationAreas` is guarded to students/admins, so pause it for guests, teachers and
  // preview mode — otherwise mounting a learning asset fires a forbidden query that breaks the flow.
  const considerationsEnabled = computed(
    () => !previewMode && !!accountStudentRole.value,
  );
  const { considerationAreas, considerationScorePercentage, refetch: refetchConsiderationAreas, error: considerationAreasError } = useConsiderationAreas(
    computed(() => !considerationsEnabled.value),
  );
  const startTime = ref<number | null>(null);
  const lastNodeId = ref<string | null>(null);
  const completing = ref(false);

  const ensureProgress = async () => {
    if (progressId.value || previewMode) {
      return;
    }
    const result = await startProgress(resolvedId.value, CompletableType.LearningAsset);
    if ('error' in result && result.error) {
      const err = result.error;
      const errorContext: Record<string, unknown> = {
        assetId: resolvedId.value,
        assetSlug: asset.value?.slug ?? null,
        assetType: asset.value?.type ?? null,
        completableType: CompletableType.LearningAsset,
        previouslyCompleted: previouslyCompleted.value,
        errorName: err instanceof Error ? err.name : null,
        errorMessage: err instanceof Error ? err.message : String(err),
      };

      if (err instanceof CombinedError) {
        errorContext.graphQLErrors = err.graphQLErrors.map(gqlErr => ({
          message: gqlErr.message,
          path: gqlErr.path ?? null,
          code: gqlErr.extensions?.code ?? null,
          category: gqlErr.extensions?.category ?? null,
          extensions: gqlErr.extensions ?? null,
        }));
        errorContext.networkError = err.networkError
          ? {
              name: err.networkError.name,
              message: err.networkError.message,
            }
          : null;
        errorContext.responseStatus = (err.response as { status?: number } | undefined)?.status ?? null;
      }

      handleError(err, {
        logMessage: 'Failed to start learning asset progress',
        context: errorContext,
      });
      return;
    }
    progressId.value = result.userProgress?.id;
  };

  const trackAssetStarted = async () => {
    startTime.value = Date.now();
    await orchestrator.notifyFlowStarted?.();
    ensureProgress();
    orchestrator.writeActivityInfoEvent?.({
      activityInfoType: CoachActivityInfoType.LearningAssetStarted,
      data: { learningAssetId: resolvedId.value, learningAssetName: asset.value?.name ?? null },
    });
    if (!previewMode) {
      analytics.track(EVENTS.UNIFIED_CONTENT_ASSET_STARTED, {
        ...baseAssetEventProps(),
        origin: options?.origin ?? null,
        previouslyCompleted: previouslyCompleted.value,
      });
    }
  };

  const starting = ref(false);

  const handleBeginContent = async () => {
    if (starting.value || hasStarted.value) {
      return;
    }
    starting.value = true;
    hasStarted.value = true;
    // The content screen must not mount until the ghost session exists — its components
    // fire authenticated queries (self/wallet) on mount, and an anonymous session gets
    // bounced to /login?sessionExpired=true by the urql UNAUTHENTICATED handler.
    await trackAssetStarted();
    screen.value = 'content';
  };

  const startWithoutOverview = (loaded: LearningAssetViewModel) => {
    if (options?.keepOverview?.(loaded)) {
      hasStarted.value = false;
      screen.value = 'overview';
      return;
    }
    trackAssetStarted();
  };

  if (options?.skipOverview && !options?.initialScreen) {
    if (asset.value) {
      startWithoutOverview(asset.value);
    }
    else {
      const stop = watch(asset, (loadedAsset) => {
        if (loadedAsset) {
          stop();
          startWithoutOverview(loadedAsset);
        }
      });
    }
  }

  const handleContentComplete = async (skillSlugs?: string[]) => {
    completing.value = true;
    const wasFreshCompletion = !previouslyCompleted.value;
    const shouldComputeConsiderations = considerationsEnabled.value && wasFreshCompletion;
    const considerationsBefore = shouldComputeConsiderations
      ? considerationAreas.value.map(area => ({ ...area }))
      : [];
    // Snapshot the overall clarity score before completion; the after-score is read from the
    // user once `completeProgress` returns (the mutation re-selects `considerationScore`).
    const clarityScoreBefore = shouldComputeConsiderations ? considerationScorePercentage.value : 0;
    if (!previewMode) {
      analytics.track(EVENTS.UNIFIED_STORY_COMPLETED, baseAssetEventProps());
    }
    orchestrator.writeActivityInfoEvent?.({
      activityInfoType: CoachActivityInfoType.LearningAssetCompleted,
      data: { learningAssetId: resolvedId.value, learningAssetName: asset.value?.name ?? null },
    });

    try {
      await ensureProgress();

      if (progressId.value && !previewMode) {
        const result = await completeProgress(progressId.value, skillSlugs);
        if ('error' in result && result.error) {
          handleError(result.error, { logMessage: 'Failed to complete learning asset progress' });
          return;
        }

        const earnedSkills = (result.userProgress?.earnedAwards ?? []).reduce<
          Array<{ id: string; name: string; iconUrl: string | null }>
        >((acc, rawAward) => {
          const earnedAward = useFragment(earnedAwardFragment, rawAward);
          if (!isSkillAward(earnedAward)) {
            return acc;
          }
          const iconFile = earnedAward.award.iconFile ? useFragment(publicFileFragment, earnedAward.award.iconFile) : null;
          acc.push({
            id: earnedAward.award.id,
            name: earnedAward.award.name,
            iconUrl: iconFile?.publicUrl ?? null,
          });
          return acc;
        }, []);

        // Only a fresh completion changes consideration-area progress. Force a network-only
        // refetch so the after-state reflects the just-committed completion (cache-and-network's
        // cached emission would read one completion stale), then pair it with the before snapshot.
        let considerations: CompletionConsideration[] = [];
        let clarity: CompletionClarity | null = null;
        if (shouldComputeConsiderations) {
          await refetchConsiderationAreas({ requestPolicy: 'network-only' });
          if (considerationAreasError.value) {
            handleError(considerationAreasError.value, {
              logMessage: 'Failed to refetch consideration areas after completion',
              context: { assetSlug: asset.value?.slug ?? null },
              toast: false,
            });
          }
          const assetConsiderationAreaIds = rawAsset.value?.considerationAreas?.map(ca => ca.id) ?? [];
          considerations = buildCompletionConsiderations({
            assetConsiderationAreaIds,
            before: considerationsBefore,
            after: considerationAreas.value,
          });

          // The clarity-bonus screen only shows when a consideration area levelled up. The
          // after-score is already live on the user (the completion mutation re-selects it).
          const leveledUp = considerations.some(consideration => consideration.afterLevel > consideration.beforeLevel);
          if (leveledUp) {
            clarity = {
              beforeScore: clarityScoreBefore,
              afterScore: considerationScorePercentage.value,
              illustration: avatarIllustration.value,
            };
          }
        }

        completionResult.value = {
          awardedCoins: result.userProgress?.awardedCoins ?? null,
          totalCoins: result.coinTransaction?.balanceAfter ?? null,
          earnedSkills,
          considerations,
          clarity,
          // Captured before the mutation refreshed the progress row: the completion screen must
          // call a first completion a first completion even when it pays nothing (CP-11267 zeroes
          // the coins, which used to be the only step and so the only "not a re-do" signal).
          wasReCompletion: !wasFreshCompletion,
        };

        trackCoinsEarned(analytics, result.coinTransaction, resolvedId.value, asset.value?.name ?? null);
      }

      const durationSeconds = computeDurationSeconds(startTime.value);

      if (!previewMode) {
        analytics.track(EVENTS.UNIFIED_CONTENT_ASSET_COMPLETED, {
          ...baseAssetEventProps(),
          origin: options?.origin ?? null,
          durationSeconds,
          coinsEarned: completionResult.value?.awardedCoins ?? null,
          previouslyCompleted: previouslyCompleted.value,
        });
      }

      await orchestrator.onAssetCompleted?.(resolvedId.value);
    }
    finally {
      completing.value = false;
      screen.value = 'sentiment';
    }
  };

  // Fired when the consideration progress screen actually renders (not at completion time),
  // so the event counts real impressions rather than every completion that has CAs.
  const trackConsiderationsViewed = () => {
    if (previewMode) {
      return;
    }
    completionResult.value?.considerations.forEach((consideration) => {
      analytics.track(EVENTS.UNIFIED_CONSIDERATIONS_AREA_PROGRESS_VIEWED, {
        ...baseAssetEventProps(),
        areaId: consideration.areaId,
        areaLabel: consideration.areaLabel,
        beforeLevel: consideration.beforeLevel,
        beforeProgress: consideration.beforeProgress,
        afterLevel: consideration.afterLevel,
        afterProgress: consideration.afterProgress,
        leveledUp: consideration.afterLevel > consideration.beforeLevel,
        origin: options?.origin ?? null,
      });
    });
  };

  // Fired when the clarity-bonus screen actually renders, so the event counts real impressions.
  const trackClarityViewed = () => {
    const clarity = completionResult.value?.clarity;
    if (previewMode || !clarity) {
      return;
    }
    analytics.track(EVENTS.UNIFIED_CONSIDERATIONS_CLARITY_VIEWED, {
      ...baseAssetEventProps(),
      beforeScore: clarity.beforeScore,
      afterScore: clarity.afterScore,
      origin: options?.origin ?? null,
    });
  };

  /**
   * How the survey screen was reached. From the coach's card it is the whole visit, so leaving it
   * closes the activity; from the slider it is one more step before the completion celebration.
   */
  const surveyFromCard = ref(options?.initialScreen === 'survey');

  /**
   * Whether this asset's survey is asked here, on the way out, rather than by the coach later. A
   * student who already answered is sent no survey at all, so a re-completion never asks twice.
   */
  const asksSurveyInFlow = computed(() =>
    !previewMode && !!assetSurvey.value?.askedAtCompletion && assetSurvey.value.questions.length > 0);

  /** The coach's card arriving while this activity is already open. */
  const openSurveyFromCard = () => {
    surveyFromCard.value = true;
    screen.value = 'survey';
  };

  const handleSentimentComplete = (sentiment?: Sentiment) => {
    if (asksSurveyInFlow.value) {
      surveyFromCard.value = false;
      screen.value = 'survey';
    }
    else {
      screen.value = 'completion';
    }
    orchestrator.writeActivityInfoEvent?.({
      activityInfoType: CoachActivityInfoType.LearningAssetSentiment,
      data: {
        learningAssetId: resolvedId.value,
        learningAssetName: asset.value?.name ?? null,
        sentiment: sentiment ?? null,
      },
    });
  };

  /**
   * Leaving the survey screen. Opened from the coach's card, the survey was the whole visit, so any
   * exit closes the activity. Reached from the slider, the activity was finished this sitting:
   * submitting or skipping moves on to the completion celebration, and closing finishes the flow
   * the way closing the completion screen does.
   */
  const leaveSurvey = ({ by }: { by: 'done' | 'close' }) => {
    if (surveyFromCard.value) {
      orchestrator.cancelFlow();
      return;
    }
    if (by === 'close') {
      orchestrator.completeFlow();
      return;
    }
    screen.value = 'completion';
  };

  /** Submit, skip, or continue past the thanks. */
  const handleSurveyDone = () => {
    leaveSurvey({ by: 'done' });
  };

  const handleCompletionDone = () => {
    orchestrator.completeFlow();
  };

  const handleCloseRequest = (): boolean => {
    if (screen.value === 'overview') {
      orchestrator.cancelFlow();
      return true;
    }
    // The survey screen is a questionnaire about an activity the student already finished:
    // closing it is a dismissal, never an abort, so nothing is confirmed and nothing is reported
    // as abandoned.
    if (screen.value === 'survey') {
      leaveSurvey({ by: 'close' });
      return true;
    }
    if (screen.value === 'sentiment' || screen.value === 'completion') {
      orchestrator.completeFlow();
      return true;
    }
    if (screen.value === 'content' && hasStarted.value) {
      showCloseConfirmation.value = true;
      return true;
    }
    orchestrator.cancelFlow();
    return true;
  };

  const confirmClose = () => {
    showCloseConfirmation.value = false;
    hasStarted.value = false;
    orchestrator.writeActivityInfoEvent?.({
      activityInfoType: CoachActivityInfoType.LearningAssetAborted,
      data: { learningAssetId: resolvedId.value, learningAssetName: asset.value?.name ?? null },
    });
    startTime.value = null;

    analytics.track(EVENTS.UNIFIED_CONTENT_ASSET_EXITED, {
      ...baseAssetEventProps(),
      lastNodeId: lastNodeId.value,
      origin: options?.origin ?? null,
    });

    orchestrator.cancelFlow();
  };

  onMounted(() => orchestrator.pushCloseHandler(handleCloseRequest));
  onUnmounted(() => orchestrator.popCloseHandler());

  return {
    asset,
    assetSurvey,
    assetSurveyCoinsAwarded,
    baseAssetEventProps,
    learningSessionId,
    screen,
    hasStarted,
    showCloseConfirmation,
    fetching,
    stale,
    error,
    completing,
    completionResult,
    lastNodeId,
    handleBeginContent,
    starting,
    handleContentComplete,
    handleSentimentComplete,
    handleSurveyDone,
    openSurveyFromCard,
    handleCompletionDone,
    handleCloseRequest,
    confirmClose,
    trackConsiderationsViewed,
    trackClarityViewed,
  };
};
